// PRD 13 §2.1 / §4.1 — Notice list table.
// Columns: NID · 대상 · 제목 · 분류 · 작성/게시자 · 조회수 · 작성/게시일시 · 상태 (+ 삭제).
// Default sort createdAt DESC. All columns sortable except 작성/게시자 (PRD §4.1).
//
// Row click opens the edit form (matches the prototype, where the whole row is
// the click target and the delete cell swallows the click). The delete column
// renders for every role that can see the page; the button itself is enabled
// only for Root (no PUBLICATION:DELETE level in the catalog) — PRD §3.4 line
// 199 / §8.3: "Root 외 전체" sees it disabled with a tooltip (SHCT-602).

import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import type { NoticeSummary } from '../types/notices.types';
import type { NoticeSortKey, SortDir } from '../store/notices-page.store';
import { formatNid, formatNoticeDateTime } from '../utils/format';
import { NoticeAudienceBadge } from './NoticeAudienceBadge';
import { NoticeAuthorCell } from './NoticeAuthorCell';
import { NoticeStatusBadge } from './NoticeStatusBadge';

interface NoticeTableProps {
  rows: NoticeSummary[];
  sortKey: NoticeSortKey;
  sortDir: SortDir;
  onSort: (key: NoticeSortKey) => void;
  onRowOpen: (row: NoticeSummary) => void;
  // PRD §7 — the Delete column is always present; the button is enabled only
  // for Root (no PUBLICATION:DELETE level). Every non-Root role sees it
  // disabled with a tooltip (PRD §3.4 line 199 / §8.3).
  deleteEnabled: boolean;
  onDelete: (row: NoticeSummary) => void;
}

const TITLE_MAX_DISPLAY = 100;

function sortIcon(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'asc' ? '↑' : '↓';
}

interface SortableHeaderProps {
  label: string;
  columnKey: NoticeSortKey;
  sortKey: NoticeSortKey;
  sortDir: SortDir;
  onSort: (key: NoticeSortKey) => void;
  style?: React.CSSProperties;
}

function SortableHeader({
  label,
  columnKey,
  sortKey,
  sortDir,
  onSort,
  style,
}: SortableHeaderProps) {
  const active = sortKey === columnKey;
  return (
    <th
      data-sort={columnKey}
      onClick={() => onSort(columnKey)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={style}
    >
      {label} <span className="sort-icon">{sortIcon(active, sortDir)}</span>
    </th>
  );
}

export function NoticeTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  onRowOpen,
  deleteEnabled,
  onDelete,
}: NoticeTableProps) {
  const { t } = useTranslation('notices');

  return (
    // SHCT-606 — fixed layout so a long title stays inside its cell instead of
    // stretching the column and pushing 상태/삭제 out of the table. Every short
    // column below is pinned; the unpinned 제목 column absorbs the rest.
    <table className="table table--clickable table--fixed">
      <thead>
        <tr>
          <SortableHeader label={t('list.columns.nid')} columnKey="id" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 80 }} />
          <SortableHeader label={t('list.columns.audience')} columnKey="audience" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 110 }} />
          <SortableHeader label={t('list.columns.title')} columnKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          <SortableHeader label={t('list.columns.category')} columnKey="category" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 120 }} />
          {/* 작성/게시자 is non-sortable per PRD §4.1. */}
          <th style={{ width: 200 }}>{t('list.columns.author')}</th>
          <SortableHeader label={t('list.columns.views')} columnKey="views" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 100 }} />
          <SortableHeader label={t('list.columns.createdAt')} columnKey="createdAt" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 160 }} />
          <SortableHeader label={t('list.columns.status')} columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 120 }} />
          <th style={{ width: 60, textAlign: 'center' }}>{t('list.columns.delete')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const title =
            row.title.length > TITLE_MAX_DISPLAY
              ? `${row.title.slice(0, TITLE_MAX_DISPLAY)}...`
              : row.title;
          return (
            <tr key={row.id} onClick={() => onRowOpen(row)}>
              {/* Prefer the server-minted NID; fall back to deriving it from id. */}
              <td>{row.nid ?? formatNid(row.id)}</td>
              <td>
                <NoticeAudienceBadge audience={row.audience} />
              </td>
              <td>
                {/* SHCT-606 — a flex row so ONLY the title truncates: the
                    푸시 발송됨 badge stays pinned and visible instead of being
                    clipped away by the title's ellipsis. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                  {/* Title truncates by character count (TITLE_MAX_DISPLAY, PRD
                      §4.1) and again by pixel width (.cell-ellipsis); either way
                      the tooltip carries the full title. */}
                  <span
                    className="link-text cell-ellipsis"
                    title={row.title}
                    style={{ minWidth: 0 }}
                  >
                    {title}
                  </span>
                  {row.pushOnPublish ? (
                    <span
                      className="badge badge-info"
                      style={{ fontSize: 10, padding: '2px 6px', flexShrink: 0 }}
                    >
                      {t('list.pushBadge')}
                    </span>
                  ) : null}
                </div>
              </td>
              <td>{t(`categories.${row.category}`)}</td>
              <td>
                <NoticeAuthorCell author={row.author} />
              </td>
              <td>{row.views.toLocaleString()}</td>
              <td>{formatNoticeDateTime(row.createdAt)}</td>
              <td>
                <NoticeStatusBadge status={row.status} />
              </td>
              {/* The cell — not just the button — swallows the click, so a
                  disabled ✕ can never fall through to the row's edit-open
                  handler (SHCT-602). */}
              <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                {/* The tooltip and the not-allowed cursor live on a wrapper
                    <span>: `.btn:disabled` sets `pointer-events: none`, so a
                    disabled <button> receives no hover and a `title` on the
                    button itself would never surface (SHCT-602). */}
                <span
                  title={deleteEnabled ? t('list.deleteRow') : t('tooltips.noDeletePermission')}
                  style={{
                    display: 'inline-block',
                    cursor: deleteEnabled ? 'pointer' : 'not-allowed',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={!deleteEnabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!deleteEnabled) return;
                      onDelete(row);
                    }}
                    aria-label={t('list.deleteRow')}
                    style={{
                      color: 'var(--destructive)',
                      padding: '2px 8px',
                      opacity: deleteEnabled ? 1 : 0.4,
                      cursor: deleteEnabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
